import React from "react";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import {
  fetchUsageMetrics,
  fetchDashboardStats,
  fetchSystemHealth,
} from "@/store/slices/adminSlice";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipProvider } from "@/components/ui/tooltip";
import {
  BarChart3,
  Activity,
  TrendingUp,
  Shield,
  RefreshCw,
  HelpCircle,
} from "lucide-react";
import { toast } from "sonner";

interface UsageMetrics {
  userEngagement: number;
  totalActiveUsers?: number;
  newUsersInPeriod?: number;
  activeUsersToday?: number;
}

interface DashboardStats {
  totalUsers: number;
  growthRate: number;
  verifiedUsers?: number;
  activeSubscriptions?: number;
}

interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  subscriptionStatus: "active" | "inactive" | "pending";
  createdAt: string;
}

interface AnalyticsTabProps {
  usageMetrics: UsageMetrics | null;
  dashboardStats: DashboardStats | null;
  users: AdminUser[];
}

export const AnalyticsTab: React.FC<AnalyticsTabProps> = ({
  usageMetrics,
  dashboardStats,
  users,
}) => {
  const dispatch = useAppDispatch();

  // Get system health from Redux
  const { systemHealth, healthLoading, healthError } = useAppSelector(
    (state) => state.admin,
  );

  // Auto-refresh system health every 30 seconds
  React.useEffect(() => {
    const interval = setInterval(() => {
      dispatch(fetchSystemHealth());
    }, 120000); // 2 minutes instead of 30 seconds to reduce rate limiting

    return () => clearInterval(interval);
  }, [dispatch]);

  // Manual refresh health function
  const refreshHealth = async () => {
    try {
      await dispatch(fetchSystemHealth()).unwrap();
      toast.success("État du système actualisé");
    } catch {
      toast.error("Erreur lors de l'actualisation de l'état du système");
    }
  };

  // Helper function to get status color and text
  const getStatusInfo = (status: string) => {
    switch (status) {
      case "operational":
      case "connected":
      case "active":
      case "healthy":
        return {
          color: "bg-green-500",
          text: "text-green-600 dark:text-green-400",
          label:
            status === "connected"
              ? "Connectée"
              : status === "active"
                ? "Active"
                : status === "healthy"
                  ? "Opérationnel"
                  : "Opérationnel",
        };
      case "degraded":
        return {
          color: "bg-yellow-500",
          text: "text-yellow-600 dark:text-yellow-400",
          label: "Dégradé",
        };
      case "down":
      case "disconnected":
      case "inactive":
      case "error":
      case "unhealthy":
        return {
          color: "bg-red-500",
          text: "text-red-600 dark:text-red-400",
          label:
            status === "disconnected"
              ? "Déconnectée"
              : status === "inactive"
                ? "Inactive"
                : "Erreur",
        };
      default:
        return {
          color: "bg-gray-500",
          text: "text-gray-600 dark:text-gray-400",
          label: "Inconnu",
        };
    }
  };

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Analyses et Rapports
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Visualisez les performances et l'utilisation de votre plateforme
          </p>
        </div>

        {/* Usage Metrics */}
        {usageMetrics && (
          <div className="space-y-6">
            {/* Main Metrics Row */}
            <Card className=" rounded-xl shadow-lg border ">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">
                    Métriques d'Utilisation (30 derniers jours)
                  </CardTitle>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    dispatch(fetchUsageMetrics("30d"));
                    dispatch(fetchDashboardStats());
                    dispatch(fetchSystemHealth());
                    toast.success("Métriques actualisées");
                  }}
                  className="opacity-90 hover:opacity-100"
                >
                  <Activity className="h-4 w-4 mr-2" />
                  Actualiser
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <Tooltip
                      content="Pourcentage d'utilisateurs actifs par rapport au total des utilisateurs inscrits"
                      maxWidth="lg"
                    >
                      <div className="cursor-help">
                        <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                          {usageMetrics.userEngagement}%
                        </p>
                        <div className="flex items-center justify-center space-x-1">
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            Engagement Utilisateur
                          </p>
                          <HelpCircle className="h-3 w-3 text-gray-400" />
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          {usageMetrics.totalActiveUsers || 0} utilisateurs
                          actifs
                        </p>
                      </div>
                    </Tooltip>
                  </div>
                  <div className="text-center">
                    <Tooltip
                      content="Nombre d'utilisateurs actifs aujourd'hui"
                      maxWidth="lg"
                    >
                      <div className="cursor-help">
                        <p className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">
                          {usageMetrics.activeUsersToday || 0}
                        </p>
                        <div className="flex items-center justify-center space-x-1">
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            Utilisateurs Actifs Aujourd'hui
                          </p>
                          <HelpCircle className="h-3 w-3 text-gray-400" />
                        </div>
                      </div>
                    </Tooltip>
                  </div>
                  <div className="text-center">
                    <Tooltip
                      content="Nombre de nouveaux utilisateurs inscrits dans les 30 derniers jours"
                      maxWidth="lg"
                    >
                      <div className="cursor-help">
                        <p className="text-3xl font-bold text-sky-600 dark:text-sky-400">
                          {usageMetrics.newUsersInPeriod || 0}
                        </p>
                        <div className="flex items-center justify-center space-x-1">
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            Nouveaux Utilisateurs
                          </p>
                          <HelpCircle className="h-3 w-3 text-gray-400" />
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          30 derniers jours
                        </p>
                      </div>
                    </Tooltip>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* System Health and Real-time Stats */}

            {/* System Health and Real-time Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Growth Metrics */}
              <Card className=" rounded-xl shadow-lg border ">
                <CardHeader>
                  <div className="flex items-center space-x-2">
                    <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                      <TrendingUp className="h-5 w-5 mr-2 text-green-500" />
                      Croissance
                    </CardTitle>
                    <Tooltip
                      content="Métriques de croissance calculées en comparant les données actuelles avec les périodes précédentes"
                      maxWidth="lg"
                    >
                      <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                    </Tooltip>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Tooltip
                      content="Pourcentage de croissance mensuelle calculé en comparant le mois actuel au mois précédent"
                      maxWidth="lg"
                    >
                      <span className="text-sm text-gray-600 dark:text-gray-400 cursor-help border-b border-dotted border-gray-400">
                        Taux de croissance
                      </span>
                    </Tooltip>
                    <span
                      className={`text-lg font-semibold ${
                        (dashboardStats?.growthRate ?? 0) >= 0
                          ? "text-green-600 dark:text-green-400"
                          : "text-red-600 dark:text-red-400"
                      }`}
                    >
                      {(dashboardStats?.growthRate ?? 0) >= 0 ? "+" : ""}
                      {dashboardStats?.growthRate ?? 0}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <Tooltip
                      content="Nombre d'utilisateurs qui se sont inscrits au cours des 30 derniers jours"
                      maxWidth="lg"
                    >
                      <span className="text-sm text-gray-600 dark:text-gray-400 cursor-help border-b border-dotted border-gray-400">
                        Nouveaux utilisateurs (30j)
                      </span>
                    </Tooltip>
                    <span className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                      {usageMetrics.newUsersInPeriod ||
                        users.filter((u) => {
                          const createdAt = new Date(u.createdAt);
                          const thirtyDaysAgo = new Date();
                          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                          return createdAt >= thirtyDaysAgo;
                        }).length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <Tooltip
                      content="Nombre total d'utilisateurs avec un abonnement actif sur la plateforme"
                      maxWidth="md"
                    >
                      <span className="text-sm text-gray-600 dark:text-gray-400 cursor-help border-b border-dotted border-gray-400">
                        Abonnements actifs
                      </span>
                    </Tooltip>
                    <span className="text-lg font-semibold text-purple-600 dark:text-purple-400">
                      {dashboardStats?.activeSubscriptions ||
                        users.filter((u) => u.subscriptionStatus === "active")
                          .length}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Platform Health Widget - Always displayed */}
              <Card className=" rounded-xl shadow-lg border ">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                      <Shield className="h-5 w-5 mr-2 text-green-500" />
                      État du Système
                    </CardTitle>
                    <Tooltip
                      content="Vérification en temps réel de l'état des services essentiels de la plateforme. Actualisation automatique toutes les 2 minutes"
                      maxWidth="lg"
                    >
                      <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                    </Tooltip>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={refreshHealth}
                    disabled={healthLoading}
                    className="opacity-90 hover:opacity-100"
                  >
                    <RefreshCw
                      className={`h-4 w-4 mr-1 ${healthLoading ? "animate-spin" : ""}`}
                    />
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  {healthLoading ? (
                    <div className="space-y-4">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between animate-pulse"
                        >
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                        </div>
                      ))}
                    </div>
                  ) : healthError ? (
                    <div className="text-center py-4">
                      <p className="text-red-600 dark:text-red-400 text-sm">
                        Erreur: {healthError}
                      </p>
                    </div>
                  ) : systemHealth ? (
                    <>
                      <div className="flex items-center justify-between">
                        <Tooltip
                          content="État de l'API principale du système et temps de réponse"
                          maxWidth="md"
                        >
                          <span className="text-sm text-gray-600 dark:text-gray-400 cursor-help border-b border-dotted border-gray-400">
                            API Status
                          </span>
                        </Tooltip>
                        <div className="flex items-center space-x-2">
                          <div
                            className={`w-2 h-2 rounded-full ${
                              getStatusInfo(systemHealth.apiStatus.status).color
                            }`}
                          ></div>
                          <span
                            className={`text-sm font-medium ${
                              getStatusInfo(systemHealth.apiStatus.status).text
                            }`}
                          >
                            {getStatusInfo(systemHealth.apiStatus.status).label}
                          </span>
                          {systemHealth.apiStatus.responseTime > 0 && (
                            <span className="text-xs text-gray-400">
                              ({systemHealth.apiStatus.responseTime}ms)
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <Tooltip
                          content="Connectivité à la base de données PostgreSQL et temps de réponse des requêtes"
                          maxWidth="lg"
                        >
                          <span className="text-sm text-gray-600 dark:text-gray-400 cursor-help border-b border-dotted border-gray-400">
                            Base de données
                          </span>
                        </Tooltip>
                        <div className="flex items-center space-x-2">
                          <div
                            className={`w-2 h-2 rounded-full ${
                              getStatusInfo(systemHealth.database.status).color
                            }`}
                          ></div>
                          <span
                            className={`text-sm font-medium ${
                              getStatusInfo(systemHealth.database.status).text
                            }`}
                          >
                            {getStatusInfo(systemHealth.database.status).label}
                          </span>
                          {systemHealth.database.responseTime > 0 && (
                            <span className="text-xs text-gray-400">
                              ({systemHealth.database.responseTime}ms)
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <Tooltip
                          content="État du service d'envoi d'emails (Nodemailer) pour les notifications et OTP"
                          maxWidth="lg"
                        >
                          <span className="text-sm text-gray-600 dark:text-gray-400 cursor-help border-b border-dotted border-gray-400">
                            Service Email
                          </span>
                        </Tooltip>
                        <div className="flex items-center space-x-2">
                          <div
                            className={`w-2 h-2 rounded-full ${
                              getStatusInfo(systemHealth.emailService.status)
                                .color
                            }`}
                          ></div>
                          <span
                            className={`text-sm font-medium ${
                              getStatusInfo(systemHealth.emailService.status)
                                .text
                            }`}
                          >
                            {
                              getStatusInfo(systemHealth.emailService.status)
                                .label
                            }
                          </span>
                          {systemHealth.emailService.responseTime > 0 && (
                            <span className="text-xs text-gray-400">
                              ({systemHealth.emailService.responseTime}ms)
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <Tooltip
                          content="Disponibilité de l'API Stripe pour les paiements et gestion des abonnements"
                          maxWidth="lg"
                        >
                          <span className="text-sm text-gray-600 dark:text-gray-400 cursor-help border-b border-dotted border-gray-400">
                            API Stripe
                          </span>
                        </Tooltip>
                        <div className="flex items-center space-x-2">
                          <div
                            className={`w-2 h-2 rounded-full ${
                              getStatusInfo(systemHealth.stripeApi.status).color
                            }`}
                          ></div>
                          <span
                            className={`text-sm font-medium ${
                              getStatusInfo(systemHealth.stripeApi.status).text
                            }`}
                          >
                            {getStatusInfo(systemHealth.stripeApi.status).label}
                          </span>
                          {systemHealth.stripeApi.responseTime > 0 && (
                            <span className="text-xs text-gray-400">
                              ({systemHealth.stripeApi.responseTime}ms)
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-gray-400 mt-2 pt-2 border-t">
                        Dernière vérification:{" "}
                        {new Date(
                          systemHealth.apiStatus.lastChecked,
                        ).toLocaleTimeString("fr-FR")}
                      </div>
                    </>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          API Status
                        </span>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            Chargement...
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Base de données
                        </span>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            Chargement...
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Service Email
                        </span>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            Chargement...
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          API Stripe
                        </span>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            Chargement...
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Additional Analytics Placeholder */}
        {!usageMetrics && (
          <Card>
            <CardContent className="p-6">
              <div className="text-center py-12">
                <BarChart3 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Chargement des Analyses
                </h3>
                <p className="text-gray-500 dark:text-gray-400">
                  Les données d'analyse sont en cours de chargement...
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </TooltipProvider>
  );
};
